import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FluentResource } from "@fluent/bundle";

import KLink from "@kikoff/components/src/v1/navigation/KLink";
import useArray from "@kikoff/hooks/src/useArray";
import useUpdateEffect from "@kikoff/hooks/src/useUpdateEffect";
import { web } from "@kikoff/proto/src/protos";
import { format } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import SectionCard from "@component/dashboard/offer/section_card";
import ViewTemplate from "@component/dashboard/offer/view_template";
import PersistentAppBar from "@component/flows/components/PersistentAppBar";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import { KCheckbox } from "@component/inputs/checkbox";
import { InfoTooltip } from "@component/tooltips/tooltips";
import { selectFeatureFlag } from "@feature/page";
import { getProductFromCheckoutPreview, shopCheckout } from "@feature/shopping";
import { getKikoffCreditAccountMentalModelName } from "@src/constants";
import { useOverlaysController } from "@src/overlay";
import { track } from "@util/analytics";
import { newReactLocalization } from "@util/l10n";

import styles from "./agreement.module.scss";

interface AgreementViewProps extends PageRegistration {}

export default function AgreementView({
  configFlow,
  Button,
}: AgreementViewProps) {
  const dispatch = useDispatch();

  const [completeList, setCompleteList] = useState([true, false]);

  const isComplete = completeList.every((complete) => complete);

  configFlow.layout(PersistentAppBar, { title: "Agreement" }).button(
    <Button
      disabled={!isComplete}
      onClick={() => {
        track.tap("CA - Activation - Activate and Purchase Button");

        return dispatch(shopCheckout());
      }}
      text={{
        pending: "Checking out...",
        success: "Checked out",
      }}
    >
      Activate account & confirm purchase
    </Button>,
    [isComplete]
  );

  const note = "The official stuff before we setup your account.";

  // NOTE(tom): This Credit Limit value is not shown, because the 2-dollar experiment is off.
  return (
    <PageAlign scroll>
      <ViewTemplate className={styles["agreement-view"]} note={note}>
        <CreditLineDetails />
        <Terms
          onCompletionChange={(complete) => {
            setCompleteList([completeList[0], complete]);
          }}
        />
      </ViewTemplate>
    </PageAlign>
  );
}

function CreditLineDetails() {
  const preview = useSelector((state) => state.shopping.checkoutPreview);
  const creditLimitValue = preview?.newCreditLimitCents;

  return (
    <SectionCard
      className={styles["credit-line-details"]}
      header="Tradeline details"
    >
      <div className={styles.info}>
        <div>
          <header>APR</header>
          <span>0%</span>
        </div>
        <div>
          <header>Annual fee</header>
          <span>{format.money(0)}</span>
        </div>
        <div>
          <header>Credit limit</header>
          <span className={styles["credit-limit-value"]}>
            <span className={styles["highlighted-value"]}>
              {format.money(creditLimitValue)}
            </span>
            <InfoTooltip
              className={styles["grey-info-tooltip"]}
              message="This is the credit we’re lending you. It can only be used for Kikoff products and will be reported as your credit limit to the bureaus."
            />
          </span>
        </div>
      </div>
      <div className="note">
        This is just saying that your account really is zero interest and zero
        hidden fees.
      </div>
    </SectionCard>
  );
}

const RESOURCES = {
  en: new FluentResource(`kikoff-credit-account-agreement = {$kikoffCreditAccount} Agreement
`),
  es: new FluentResource(`kikoff-credit-account-agreement = Acuerdo de {$kikoffCreditAccount}
`),
};

const l10n = newReactLocalization(RESOURCES);

interface TermsProps {
  onCompletionChange;
}

export function CreditAccountAgreementLink() {
  const checkoutPreview = useSelector(
    (state) => state.shopping.checkoutPreview
  );
  const overlays = useOverlaysController();

  const product = getProductFromCheckoutPreview(checkoutPreview);

  return (
    <KLink
      href={product?.creditLineAgreement?.agreementUrl}
      variant="primary"
      type="pdf"
    >
      Kikoff Credit Account Agreement
    </KLink>
  );
}

interface CreditAccountAgreementLinkProductProps {
  product: web.public_.IProduct;
}

export function CreditAccountAgreementLinkProduct({
  product,
}: CreditAccountAgreementLinkProductProps) {
  const showPreview = useSelector(
    selectFeatureFlag("show_signed_signatures_preview")
  );

  const overlays = useOverlaysController();
  return (
    <KLink
      href={product?.creditLineAgreement?.agreementUrl}
      type="pdf"
      variant="primary"
    >
      Kikoff Credit Account Agreement
    </KLink>
  );
}

const Terms: React.FC<TermsProps> = ({ onCompletionChange }) => {
  const [checks, setChecks] = useArray([false, false]);

  const complete = checks.every((check) => check);

  useUpdateEffect(() => {
    onCompletionChange(complete);
  }, [complete]);

  return (
    <SectionCard header="Review & Agree to Terms">
      <div className={styles["review-agreement-copy"]}>
        We’re a Licensed Lender and report your account to the credit bureaus -
        please review the <CreditAccountAgreementLink />.
      </div>
      <KCheckbox
        className={styles["agree-checkbox"]}
        checked={checks[0]}
        onChange={(e) => {
          track.tap("CA - Activation - Agree to Terms");
          setChecks.at(0, e.currentTarget.checked);
        }}
      >
        I’ve read and agree to the terms.
      </KCheckbox>
      <KCheckbox
        className={styles["agree-checkbox"]}
        checked={checks[1]}
        onChange={(e) => {
          track.tap("CA - Activation - I Understand");
          setChecks.at(1, e.currentTarget.checked);
        }}
      >
        I understand I will be opening a{" "}
        {getKikoffCreditAccountMentalModelName()} and will be responsible for
        monthly payments.
      </KCheckbox>
    </SectionCard>
  );
};
